import { useEffect, useRef, useState } from 'react';

interface DropdownPosition {
  top: number;
  left: number;
  openUpward: boolean;
}

interface UseDropdownPositionOptions {
  isOpen: boolean;
  buttonRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  dropdownWidth?: number;
  dropdownHeight?: number;
  gap?: number;
}

/**
 * Custom hook for managing dropdown positioning and outside click detection
 * Automatically positions dropdown to avoid viewport overflow
 */
export function useDropdownPosition({
  isOpen,
  buttonRef,
  onClose,
  dropdownWidth = 320,
  dropdownHeight = 200,
  gap = 8,
}: UseDropdownPositionOptions) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<DropdownPosition>({
    top: 0,
    left: 0,
    openUpward: false,
  });
  const [isPositionReady, setIsPositionReady] = useState(false);

  // Calculate and update dropdown position
  useEffect(() => {
    if (!isOpen || !buttonRef.current) {
      setIsPositionReady(false);
      return;
    }

    const updatePosition = () => {
      if (!buttonRef.current) return;

      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      // Check if there's enough space below
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const openUpward = spaceBelow < dropdownHeight + gap && spaceAbove > spaceBelow;

      // Calculate vertical position
      let top = openUpward 
        ? buttonRect.top - gap 
        : buttonRect.bottom + gap;

      // Calculate horizontal position (align to button right edge)
      let left = buttonRect.right - dropdownWidth;
      
      // Ensure dropdown stays within viewport horizontally
      if (left < gap) {
        left = gap;
      } else if (left + dropdownWidth > viewportWidth - gap) {
        left = viewportWidth - dropdownWidth - gap;
      }

      setPosition({ top, left, openUpward });
      setIsPositionReady(true);
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, buttonRef, dropdownWidth, dropdownHeight, gap]);

  // Handle outside clicks
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        buttonRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, buttonRef, onClose]);

  return { dropdownRef, position, isPositionReady };
}
